import { docsRoutes } from 'constants/navigation';
import { copyTextToClipboard } from 'helpers/clipboard';
import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './Docs.module.scss';
import { DOC_PAGES, DocVideo, findDocPage } from './Docs.constants';
import DocsToc from './DocsToc';

/**
 * Copies the doc's markdown source rather than the rendered text, so what
 * lands on the clipboard is usable as markdown.
 */
function CopyMarkdownButton({ markdown }: { markdown: string }) {
  const [justCopied, setJustCopied] = useState(false);
  const { handleMsgType } = useNotification();

  // Awaited rather than fire-and-forget: the clipboard write can be refused
  // (an unfocused document, a denied permission), and confirming a copy that
  // did not happen is worse than saying nothing.
  const handleCopy = async () => {
    try {
      await copyTextToClipboard(markdown);
      setJustCopied(true);
      window.setTimeout(() => setJustCopied(false), 2000);
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Could not copy to the clipboard',
        message: 'Select the page text and copy it directly instead.',
        dismissable: true,
      });
    }
  };

  return (
    <Button variant="secondary" size="medium" onClick={handleCopy}>
      {justCopied ? 'Copied' : 'Copy as Markdown'}
    </Button>
  );
}

/**
 * The page's walkthrough, above the prose.
 *
 * The wrapper holds the aspect ratio so the frame scales with the prose column
 * instead of being pinned to a fixed height, which would letterbox it on a
 * narrow window.
 */
function DocVideoEmbed({ video }: { video: DocVideo }) {
  return (
    <div className={styles.video}>
      <iframe
        className={styles.videoFrame}
        src={video.embedUrl}
        // Named, because an unlabelled frame reads as "unnamed iframe" to a
        // screen reader and there is nothing else on the page describing it.
        title={video.title}
        allowFullScreen
      />
    </div>
  );
}

/**
 * Renders one doc, plus its table of contents.
 *
 * `/docs/coderbyte.md` resolves to the same page as `/docs/coderbyte` so older
 * links keep working, but there is no separate raw view — the markdown source
 * is reachable through the copy button, and lives as a real `.md` file in the
 * repo for anything reading the codebase.
 *
 * Returns a fragment rather than a wrapper: the children are columns of the
 * grid in `Docs`, so an element around them would collapse the layout.
 */
export function DocPage() {
  const { slug } = useParams<{ slug: string }>();
  const page = findDocPage(slug);

  if (!page) {
    return <Navigate to={docsRoutes.page.url(DOC_PAGES[0].slug)} replace />;
  }

  return (
    <>
      <div className={styles.container}>
        {/* The doc's own H1 is the title, so the header carries only actions —
            a summary here would sit above the heading it describes. */}
        <div className={styles.header}>
          <CopyMarkdownButton markdown={page.markdown} />
        </div>

        {/* Above the prose rather than after the doc's own H1: the walkthrough
            is the fastest way through this page, so it should not need
            scrolling to find. */}
        {page.video && <DocVideoEmbed video={page.video} />}

        <div className={styles.markdown}>
          {/* rehype-slug stamps ids onto the headings so the contents list can
              link to them. */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSlug]}
          >
            {page.markdown}
          </ReactMarkdown>
        </div>
      </div>

      <DocsToc markdown={page.markdown} />
    </>
  );
}

export default DocPage;
